import { useDeferredValue, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { PersonActivityStatus, type PersonActivityStatusValue } from "@convex/schemaEnums";
import { oneOf, searchUpdate, text } from "@/lib/search";
import { FilterCount } from "../components/FilterCount";
import { statusPresentation } from "../components/activityStatus";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import type { Id } from "@convex/_generated/dataModel";
import { createColumnHelper } from "@tanstack/react-table";
import {
  LuSquareCheck,
  LuListChecks,
  LuUserCog,
  LuSend,
  LuTrash2,
  LuUsersRound,
} from "react-icons/lu";
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CloseButton,
  Dialog,
  Field,
  HStack,
  Icon,
  IconButton,
  NativeSelect,
  Portal,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react";
import { api } from "@convex/_generated/api";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { useViewerAccess } from "@/lib/access";
import {
  AssignmentAdminActions,
  AssignmentAdminDialogs,
  PersonAdminDialog,
  type AssignmentAdminAction,
} from "./ActivityAdminActions";
import { AssignActivityDrawer } from "./ActivityAssignDrawer";
import { TableSkeleton } from "../components/TableSkeleton";
import { SearchInput } from "../components/SearchInput";
import { PageHeader } from "../components/PageHeader";
import { DrawerSidebar } from "../components/DrawerSidebar";
import { DataTable } from "../components/DataTable";
import {
  ActivityAdminAccessDenied,
  ActivityAdminLoadingState,
  formatDay,
  tagStyle,
  type PersonActivityRow,
  type PersonOption,
  type ActivitySummary,
} from "./ActivityAdminShared";

const breadcrumbs = [
  { label: "CeMMbase", to: "/" },
  { label: "Activities", to: "/activities" },
  { label: "Assigned activities" },
];


const personActivityColumnHelper = createColumnHelper<PersonActivityRow>();

// Defined once, outside the component. An inline arrow is a new
// function on every render, and a table told its row identity has
// changed cannot skip anything.
const getRowId = (row: PersonActivityRow) => String(row._id);

// By column id. The activity takes what is left.
const COLUMN_WIDTHS: Record<string, string> = {
  personName: "16%",
  status: "14%",
  dueDay: "10%",
  required: "10%",
  tags: "20%",
  actions: "8.5rem",
};

export type AssignmentsSearch = {
  q?: string;
  status?: PersonActivityStatusValue;
  /** An activity's title: copies are matched by what they were assigned as. */
  activity?: string;
  /** A tag's key, as on the catalogue. */
  tag?: string;
};

export function validateAssignmentsSearch(search: Record<string, unknown>): AssignmentsSearch {
  return {
    q: text(search.q),
    status: oneOf(search.status, Object.values(PersonActivityStatus)),
    activity: text(search.activity),
    tag: text(search.tag),
  };
}

export function ActivityAssignmentsPage() {
  const access = useViewerAccess();
  const isActivityAdmin = access?.isActivityAdmin ?? false;

  const catalogue = useQuery(api.activities.listActivities, isActivityAdmin ? {} : "skip") as ActivitySummary[] | undefined;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [personActionsOpen, setPersonActionsOpen] = useState(false);
  // The row whose admin dialog is open, and which one.
  const [adminTarget, setAdminTarget] = useState<
    { row: PersonActivityRow; action: Exclude<AssignmentAdminAction, "delete"> } | null
  >(null);

  // The whole directory -- half a megabyte -- only once something needs to
  // pick a person from it, not to show the table. Loaded with the page, it
  // also arrived after the rows and made the table draw itself again.
  const needsDirectory = drawerOpen || personActionsOpen || adminTarget?.action === "reassign";
  const directory = useQuery(api.persons.listCeMMies, isActivityAdmin && needsDirectory ? {} : "skip");
  const persons: PersonOption[] | undefined = directory?.persons;
  const personActivityRows = useQuery(api.personActivities.listAllForAdmin, isActivityAdmin ? {} : "skip") as PersonActivityRow[] | undefined;

  const deleteAssignment = useMutation(api.personActivities.deletePersonActivity);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // In the address, so a narrowed list can be linked to and survives a reload.
  const params = useSearch({ from: "/_authed/activities/admin/assigned" });
  const statusFilter = params.status ?? "all";
  const activityFilter = params.activity ?? "all";
  const tagFilter = params.tag ?? "all";
  const search = params.q ?? "";
  const navigate = useNavigate({ from: "/activities/admin/assigned" });
  const setParams = (patch: AssignmentsSearch) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), ...searchUpdate });
  const isFiltered = statusFilter !== "all" || activityFilter !== "all" || tagFilter !== "all" || search.trim() !== "";

  // The input stays immediate; the filtering lags behind it. Typing "gold"
  // used to re-filter four hundred rows and rebuild the table four times, so
  // the field itself stuttered -- the one thing that must never lag, because
  // the person is looking straight at it. React keeps the last result on
  // screen until the new one is ready.
  const deferredSearch = useDeferredValue(search);

  const filteredRows = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    return (personActivityRows ?? []).filter((assignment) => {
      const matchesStatus = statusFilter === "all" || assignment.status === statusFilter;
      const matchesActivity = activityFilter === "all" || assignment.titleSnapshot === activityFilter;
      const matchesTag = tagFilter === "all" || assignment.tagSnapshots.some((tag) => tag.key === tagFilter);
      const matchesSearch = query.length === 0
        || assignment.titleSnapshot.toLowerCase().includes(query)
        || assignment.personName.toLowerCase().includes(query);
      return matchesStatus && matchesActivity && matchesTag && matchesSearch;
    });
  }, [personActivityRows, deferredSearch, statusFilter, activityFilter, tagFilter]);

  // The tags the assignments carry, as their snapshots name them: a tag
  // renamed in the catalogue since is still found under the name it had.
  const tagOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const row of personActivityRows ?? []) {
      for (const tag of row.tagSnapshots) {
        byKey.set(tag.key, tag.name);
      }
    }
    return [...byKey].sort((left, right) => left[1].localeCompare(right[1]));
  }, [personActivityRows]);




  async function handleDeleteAssignment() {
    if (!confirmDeleteId) return;
    try {
      await deleteAssignment({ personActivityId: confirmDeleteId as Id<"personActivities"> });
      toastSuccess("Assignment deleted.");
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Deleting assignment failed.");
    } finally {
      setConfirmDeleteId(null);
    }
  }

  const confirmDeleteRow = confirmDeleteId
    ? (personActivityRows ?? []).find((a) => String(a._id) === confirmDeleteId)
    : null;

  const personActivityColumns = useMemo(
    () => [
      personActivityColumnHelper.accessor("titleSnapshot", {
        header: "Activity",
        // The title is the way in. The table lists assignments and every other
        // view of one is reachable by clicking it; this was the one place that
        // showed an assignment and offered no way to open it.
        cell: ({ row, getValue }) => (
          <Stack gap={1}>
            <Link
              to="/activities/$personActivityId"
              params={{ personActivityId: String(row.original._id) }}
              search={{ from: "assigned" }}
            >
              <Text fontWeight="semibold" _hover={{ textDecoration: "underline" }}>
                {getValue()}
              </Text>
            </Link>
            {/* Assigned is the usual case and goes unsaid; the exception is
                named. */}
            {row.original.origin === "selfEnrolled" ? (
              <Text fontSize="xs" color="fg.muted">Self-enrolled</Text>
            ) : null}
          </Stack>
        ),
      }),
      personActivityColumnHelper.accessor("personName", {
        header: "Person",
        cell: ({ row }) => (
          <Text fontWeight="medium">{row.original.personName}</Text>
        ),
      }),
      personActivityColumnHelper.accessor("status", {
        header: "Status",
        // The status colours and icons the activity page and the matrix use.
        // Waiting on an opening day, cancelled and archived are not in that
        // scheme's words, so those keep the server's.
        cell: ({ row, getValue }) => {
          const look = statusPresentation(getValue());
          const ownWords = ["blocked", "cancelled", "archived"].includes(getValue());
          return (
            <Badge colorPalette={look.tone} variant="subtle" gap={1.5} whiteSpace="normal">
              <Icon as={look.icon} boxSize={3} />
              {ownWords ? row.original.statusLabel : look.label}
            </Badge>
          );
        },
      }),
      personActivityColumnHelper.accessor("dueDay", {
        header: "Due date",
        cell: ({ getValue }) => (
          <Text fontSize="sm" color="fg.muted" whiteSpace="nowrap">{formatDay(getValue())}</Text>
        ),
      }),
      personActivityColumnHelper.accessor("required", {
        header: "Scope",
        cell: ({ getValue }) => (
          // Filled for required, an outline for encouraged: as the step
          // markers on an activity's timeline are.
          <Badge variant={getValue() ? "subtle" : "outline"} colorPalette="gray">
            {getValue() ? "Required" : "Encouraged"}
          </Badge>
        ),
      }),
      personActivityColumnHelper.display({
        id: "tags",
        header: "Tags",
        cell: ({ row }) => (
          <HStack flexWrap="wrap" gap={2}>
            {row.original.tagSnapshots.length === 0 ? (
              <Text fontSize="xs" color="fg.muted">—</Text>
            ) : row.original.tagSnapshots.map((tag) => (
              <Badge key={tag.tagId} variant="outline" whiteSpace="normal" style={tagStyle(tag.color)}>
                {tag.name}
              </Badge>
            ))}
          </HStack>
        ),
      }),
      personActivityColumnHelper.display({
        id: "actions",
        header: () => <Box textAlign="right">Actions</Box>,
        cell: ({ row }) => (
          <AssignmentAdminActions
            onAction={(action) => action === "delete"
              ? setConfirmDeleteId(String(row.original._id))
              : setAdminTarget({ row: row.original, action })}
          />
        ),
      }),
    ],
    // Nothing from the page's data: the columns are built once, and a row
    // re-renders only when its own data changes.
    [],
  );

  if (access === undefined) {
    return <ActivityAdminLoadingState title="Assigned activities" description="Checking your access to the assignment administration workspace." breadcrumbs={breadcrumbs} />;
  }

  if (!isActivityAdmin) {
    return <ActivityAdminAccessDenied title="Assigned activities" description="This page is for activity admins." breadcrumbs={breadcrumbs} />;
  }

  return (
    <>
      <Box minH="full" bg="bg.subtle">
        <PageHeader
          title="Assigned activities"
          description="Every activity assigned to someone, with its status and due date."
          breadcrumbs={breadcrumbs}
          actions={
            <HStack flexWrap="wrap" gap={3}>
              <Button variant="outline" onClick={() => setPersonActionsOpen(true)}>
                <LuUserCog />
                Act on a person
              </Button>
              <Button colorPalette="blue" onClick={() => setDrawerOpen(true)}>
                <LuListChecks />
                Assign activities
              </Button>
            </HStack>
          }
        />

        <Stack gap={6} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>

          {/* The controls straight above the table, as on every other
              filtered table; the count with the table it describes. */}
          <Stack direction={{ base: "column", lg: "row" }} gap={3}>
            <SearchInput
              flex="1"
              placeholder="Search by activity or person"
              value={search}
              onChange={(next) => setParams({ q: next === "" ? undefined : next })}
            />
            <NativeSelect.Root size="sm" w={{ base: "full", lg: "14rem" }}>
              <NativeSelect.Field
                aria-label="Status"
                value={statusFilter}
                onChange={(event) => {
                  const value = event.target.value;
                  setParams({ status: value === "all" ? undefined : value as PersonActivityStatusValue });
                }}
              >
                <option value="all">All statuses</option>
                {[...new Map((personActivityRows ?? []).map((assignment) => [assignment.status, assignment.statusLabel]))].map(([status, label]) => (
                  <option key={status} value={status}>{label}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <NativeSelect.Root size="sm" w={{ base: "full", lg: "18rem" }}>
              <NativeSelect.Field
                aria-label="Activity"
                value={activityFilter}
                onChange={(event) => {
                  const value = event.target.value;
                  setParams({ activity: value === "all" ? undefined : value });
                }}
              >
                <option value="all">All activities</option>
                {[...new Set((personActivityRows ?? []).map((assignment) => assignment.titleSnapshot))].sort().map((title) => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <NativeSelect.Root size="sm" w={{ base: "full", lg: "14rem" }}>
              <NativeSelect.Field
                aria-label="Tag"
                value={tagFilter}
                onChange={(event) => {
                  const value = event.target.value;
                  setParams({ tag: value === "all" ? undefined : value });
                }}
              >
                <option value="all">All tags</option>
                {tagOptions.map(([key, name]) => (
                  <option key={key} value={key}>{name}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Stack>

          {personActivityRows === undefined ? (
            <TableSkeleton rows={9} columns={6} />
          ) : (
            <Stack gap={2}>
            <FilterCount
              shown={filteredRows.length}
              total={personActivityRows.length}
              noun={["assignment", "assignments"]}
              filtered={isFiltered}
              onClear={() => void navigate({ search: {}, ...searchUpdate })}
            />
            <Box as="section" borderWidth="1px" borderColor="border" shadow="sm">
              <DataTable
                data={filteredRows}
                columns={personActivityColumns}
                getRowId={getRowId}
                emptyMessage="No assigned activities match the current filters."
                // Only the rows in view are in the page; it still scrolls as
                // one page. Rendering all of them -- in batches, as before --
                // meant four hundred rows of badges and menus, and froze the
                // page for seconds once the second batch went in.
                stickyHeader
                virtualized="window"
                // Fixed, in proportions: see DataTable.
                columnWidths={COLUMN_WIDTHS}
                estimatedRowHeight={53}
              />
            </Box>
            </Stack>
          )}
        </Stack>
      </Box>

      <AssignActivityDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        catalogue={catalogue}
        persons={persons}
      />

      <AssignmentAdminDialogs
        target={adminTarget}
        persons={persons}
        onClose={() => setAdminTarget(null)}
      />

      <PersonAdminDialog
        open={personActionsOpen}
        onClose={() => setPersonActionsOpen(false)}
        persons={persons}
        rows={personActivityRows}
      />

      {/* ── Delete confirmation modal ── */}
      <Dialog.Root open={Boolean(confirmDeleteId)} onOpenChange={(e) => { if (!e.open) setConfirmDeleteId(null); }} role="alertdialog">
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="md">
              <Dialog.Header>
                <Dialog.Title color="red.500">Delete assignment</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                <Text>
                  Are you sure you want to permanently delete the assignment
                  {confirmDeleteRow ? (
                    <>
                      {" "}<Text as="strong" fontWeight="bold">{confirmDeleteRow.titleSnapshot}</Text> for{" "}
                      <Text as="strong" fontWeight="bold">{confirmDeleteRow.personName}</Text>
                    </>
                  ) : null}
                  ? This will also remove all related steps and reviews. This action cannot be undone.
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <ButtonGroup gap={2}>
                  <Button variant="ghost" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                  <Button colorPalette="red" onClick={() => void handleDeleteAssignment()}>
                    <LuTrash2 />
                    Delete permanently
                  </Button>
                </ButtonGroup>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}
